import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep, isEqual } from "lodash"

import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import Icon from "@/components/icon/Icon"
import {
  useFilterPresetsQuery,
  useFilterPresetDetailQuery,
  useCreateFilterPresetMutation,
  useUpdateFilterPresetMutation,
  useDeleteFilterPresetMutation,
} from "@/hooks/api/useFilterPresetQuery"
import { useSeasonWeekQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"
import { ACTIVITY_TYPE_OPTIONS, MATCH_DAY_OPTIONS } from "./constants"
import { createGlobalStore } from "@/stores/global"
import type { UpsertFilterPresetPayload } from "@/types/filterPreset"

import FilterPresetTable, { FilterPresetRow } from "./FilterPresetTable"
import FilterPresetEditor from "./FilterPresetEditor"
import { DEFAULT_PRESET_PAYLOAD } from "./constants"

import "./filterPreset.scss"

export default function FilterPresetPage() {
  const { t } = useTranslation()
  const setSnackBar = createGlobalStore((s) => s.setSnackBar)

  // --- State ---
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null)
  const [form, setForm] = useState<UpsertFilterPresetPayload>({ ...DEFAULT_PRESET_PAYLOAD })
  const [originalForm, setOriginalForm] = useState<UpsertFilterPresetPayload>({
    ...DEFAULT_PRESET_PAYLOAD,
  })
  const [isNewPreset, setIsNewPreset] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // --- Queries ---
  const { data: presets, isLoading } = useFilterPresetsQuery()
  const { data: presetDetail } = useFilterPresetDetailQuery(selectedPresetId)

  // --- Mutations ---
  const { mutate: createPreset, isPending: isCreating } = useCreateFilterPresetMutation()
  const { mutate: updatePreset, isPending: isUpdating } = useUpdateFilterPresetMutation()
  const { mutate: deletePreset, isPending: isDeleting } = useDeleteFilterPresetMutation()
  const isSaving = isCreating || isUpdating

  // 초기 로드 완료 여부 (한 번만 자동 선택)
  const isInitialized = useRef(false)

  useEffect(() => {
    if (isInitialized.current) return
    if (presets?.length) {
      setSelectedPresetId(presets[0].idfPreset)
      isInitialized.current = true
    }
  }, [presets])

  // 선택된 프리셋 상세 로드 시 폼 동기화
  useEffect(() => {
    if (!presetDetail) return
    const loaded: UpsertFilterPresetPayload = {
      name: presetDetail.name,
      reportType: presetDetail.reportType,
      idfTemplate: presetDetail.idfTemplate,
      idfPdfTemplate: presetDetail.idfPdfTemplate,
      periodType: presetDetail.periodType,
      customDateStart: presetDetail.customDateStart,
      customDateEnd: presetDetail.customDateEnd,
      activityTypes: [...presetDetail.activityTypes],
      seasonWeekIds: [...presetDetail.seasonWeekIds],
      matchDayTypes: [...presetDetail.matchDayTypes],
      selectedPlayerIds: [...presetDetail.selectedPlayerIds],
    }
    setForm(loaded)
    setOriginalForm(cloneDeep(loaded))
    setIsNewPreset(false)
  }, [presetDetail])

  // --- Derived ---
  const hasChanges = useMemo(() => !isEqual(form, originalForm), [form, originalForm])

  const isValid = useMemo(() => {
    if (!form.name.trim()) return false
    if (!form.idfTemplate) return false
    if (form.periodType === "custom") {
      if (!form.customDateStart || !form.customDateEnd) return false
      if (form.customDateStart > form.customDateEnd) return false
    }
    return true
  }, [form])

  const showEditor = selectedPresetId !== null || isNewPreset

  // 테이블 row: 새 프리셋 생성 중이면 맨 아래에 임시 row 추가
  const tableData: FilterPresetRow[] = useMemo(() => {
    const base: FilterPresetRow[] = (presets ?? []).map((p) => ({
      idfPreset: p.idfPreset,
      name: p.name,
    }))
    if (isNewPreset) base.push({ idfPreset: null, name: form.name })
    return base
  }, [presets, isNewPreset, form.name])

  const tableSelectedId: number | string | null = isNewPreset ? form.name : selectedPresetId

  // --- Handlers ---
  const updateForm = useCallback((updates: Partial<UpsertFilterPresetPayload>) => {
    setForm((prev) => ({ ...prev, ...updates }))
  }, [])

  const handleSelectRow = (row: FilterPresetRow) => {
    if (row.idfPreset === null) return // 새 프리셋 임시 row
    setSelectedPresetId(row.idfPreset)
    setIsNewPreset(false)
  }

  const { data: seasonWeekData } = useSeasonWeekQuery()
  const { data: players } = usePlayersQuery()

  const handleCreateNew = () => {
    setSelectedPresetId(null)
    setIsNewPreset(true)
    const nextNum = (presets?.length ?? 0) + 1

    const activeSeasonWeekIds = (seasonWeekData?.seasonWeeks ?? [])
      .filter((sw) => sw.active)
      .map((sw) => sw.idfSeasonWeek as number)
    // 신규 프리셋 기본값 — 전체 선택으로 시작
    const blank: UpsertFilterPresetPayload = {
      ...DEFAULT_PRESET_PAYLOAD,
      name: t("newPresetDefaultName", { number: nextNum, defaultValue: `New Preset ${nextNum}` }),
      activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value),
      seasonWeekIds: [...activeSeasonWeekIds, -1], // NONE_SEASON_WEEK_ID 포함
      matchDayTypes: [...MATCH_DAY_OPTIONS],
      selectedPlayerIds: (players ?? []).map((p) => p.idfPlayer),
    }
    setForm(blank)
    setOriginalForm(cloneDeep(blank))
  }

  const handleSave = () => {
    if (!isValid) return
    const payload: UpsertFilterPresetPayload = { ...form, name: form.name.trim() }

    const onSuccess = (data: any) => {
      setSnackBar({
        open: true,
        message: t("messages.successSave", "Successfully saved"),
        severity: "success",
      })
      if (isNewPreset && data?.response?.idfPreset) {
        setSelectedPresetId(data.response.idfPreset)
      }
      setIsNewPreset(false)
      setOriginalForm(cloneDeep(payload))
    }

    const onError = () => {
      setSnackBar({
        open: true,
        message: t("filterPresetSaveFailed", "Failed to save filter preset."),
        severity: "error",
      })
    }

    if (isNewPreset) {
      createPreset(payload, { onSuccess, onError })
    } else if (selectedPresetId) {
      updatePreset({ idfPreset: selectedPresetId, payload }, { onSuccess, onError })
    }
  }

  const handleDelete = () => {
    const isExistingPreset = selectedPresetId !== null && !isNewPreset
    if (!isExistingPreset) {
      setDeleteConfirmOpen(false)
      setSelectedPresetId(null)
      setIsNewPreset(false)
      setForm({ ...DEFAULT_PRESET_PAYLOAD })
      setOriginalForm({ ...DEFAULT_PRESET_PAYLOAD })
      return
    }
    if (!selectedPresetId) return
    deletePreset(selectedPresetId, {
      onSuccess: () => {
        setSnackBar({
          open: true,
          message: t("filterPresetDeleted", "Filter preset deleted."),
          severity: "success",
        })
        setDeleteConfirmOpen(false)

        const remaining = (presets ?? []).filter((p) => p.idfPreset !== selectedPresetId)
        if (remaining.length > 0) {
          setSelectedPresetId(remaining[0].idfPreset)
        } else {
          setSelectedPresetId(null)
          setForm({ ...DEFAULT_PRESET_PAYLOAD })
          setOriginalForm({ ...DEFAULT_PRESET_PAYLOAD })
        }
      },
      onError: () => {
        setSnackBar({
          open: true,
          message: t("filterPresetDeleteFailed", "Failed to delete filter preset."),
          severity: "error",
        })
      },
    })
  }

  return (
    <div className="FilterPresetPageCont">
      <div className="filter-preset-header">
        <h3 className="settings-section-title">{t("filterPreset", "Filter Preset")}</h3>
        <BasicButton type="default-outlined" size="sm" onClick={handleCreateNew}>
          <Icon type="add" size={16} />
          {t("newFilterPreset", "New Filter Preset")}
        </BasicButton>
      </div>

      <div
        className="FilterPresetSplitCont"
        style={!showEditor ? { gridTemplateColumns: "1fr" } : undefined}>
        <FilterPresetTable
          data={tableData}
          selectedId={tableSelectedId}
          onSelect={handleSelectRow}
          isLoading={isLoading}
        />

        {showEditor && (
          <FilterPresetEditor
            form={form}
            updateForm={updateForm}
            onSave={handleSave}
            onDelete={() => setDeleteConfirmOpen(true)}
            isSaving={isSaving}
            isDeleting={isDeleting}
            isValid={isValid}
            hasChanges={hasChanges}
            isExisting={selectedPresetId !== null && !isNewPreset}
          />
        )}
      </div>

      <Dialog
        open={deleteConfirmOpen}
        title={t("confirm")}
        maxWidth="400px"
        onClose={() => setDeleteConfirmOpen(false)}
        footer={
          <>
            <BasicButton
              type="default-outlined"
              size="md"
              onClick={() => setDeleteConfirmOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton type="danger-outlined" size="md" onClick={handleDelete}>
              {t("button.delete")}
            </BasicButton>
          </>
        }>
        <p>{t("messages.confirmDelete")}</p>
      </Dialog>
    </div>
  )
}
